import React, { useEffect, useState } from 'react';
import {
    Box,
    Dialog,
    DialogActions,
    DialogContent,
    DialogTitle,
    Button,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Tooltip,
    IconButton,
    Typography
} from '@mui/material';
import LaunchRoundedIcon from '@mui/icons-material/LaunchRounded';
import {
    getLiveProjectChaserThresholds,
    getLiveProjectChaserTone,
    readLiveProjectChaserThresholds,
    USER_PREFERENCES_UPDATED_EVENT
} from './liveProjectChaserPreferences';

const ELAPSED_TONES = {
    green: { backgroundColor: '#dcfce7', color: '#166534', borderColor: '#86efac' },
    amber: { backgroundColor: '#fef3c7', color: '#92400e', borderColor: '#fcd34d' },
    red: { backgroundColor: '#fee2e2', color: '#b91c1c', borderColor: '#fca5a5' },
    unknown: { backgroundColor: '#f3f4f6', color: '#4b5563', borderColor: '#d1d5db' }
};

function getElapsedDays(value, now = new Date()) {
    const issuedAt = new Date(value);
    if (Number.isNaN(issuedAt.getTime())) return null;
    return Math.max(0, Math.floor((now.getTime() - issuedAt.getTime()) / 86400000));
}

function formatElapsedTime(days) {
    if (!Number.isFinite(days)) return 'Unknown';
    if (days === 0) return 'Today';
    return `${days} day${days === 1 ? '' : 's'}`;
}

export default function ConfirmLiveProjectsDialog({ action, onClose }) {
    const projects = Array.isArray(action?.affected_projects) ? action.affected_projects : [];
    const [thresholds, setThresholds] = useState(() => readLiveProjectChaserThresholds());

    useEffect(() => {
        function handlePreferencesUpdated(event) {
            setThresholds(event?.detail
                ? getLiveProjectChaserThresholds(event.detail)
                : readLiveProjectChaserThresholds());
        }
        window.addEventListener(USER_PREFERENCES_UPDATED_EVENT, handlePreferencesUpdated);
        return () => window.removeEventListener(USER_PREFERENCES_UPDATED_EVENT, handlePreferencesUpdated);
    }, []);

    function openInformationPortal(reference) {
        const params = new URLSearchParams({
            portal: 'surveyor',
            reference: String(reference || '').trim(),
            page: 'initial'
        });
        window.location.assign(`${window.location.pathname}?${params.toString()}`);
    }

    return (
        <Dialog open={Boolean(action)} onClose={onClose} fullWidth maxWidth="md">
            <DialogTitle>Confirm Live Projects</DialogTitle>
            <DialogContent dividers>
                <Typography variant="body2" sx={{ color: 'text.secondary', mb: 2 }}>
                    These projects have an issued Initial Report but have not yet progressed to an Interim Site Visit.
                </Typography>
                <TableContainer sx={{ overflowX: 'auto' }}>
                    <Table size="small" aria-label="Projects awaiting live confirmation" sx={{ minWidth: 650 }}>
                        <TableHead>
                            <TableRow>
                                <TableCell>Project reference</TableCell>
                                <TableCell>Project name</TableCell>
                                <TableCell>Funder</TableCell>
                                <TableCell>Time elapsed</TableCell>
                                <TableCell align="right">Portal</TableCell>
                            </TableRow>
                        </TableHead>
                        <TableBody>
                            {projects.map((project) => {
                                const elapsedDays = getElapsedDays(project.initial_report_issued_at);
                                const tone = getLiveProjectChaserTone(elapsedDays, thresholds);
                                return (
                                    <TableRow key={project.reference}>
                                        <TableCell sx={{ fontWeight: 800 }}>{project.reference}</TableCell>
                                        <TableCell>{project.project_name || 'Not recorded'}</TableCell>
                                        <TableCell>{project.funder || 'Not recorded'}</TableCell>
                                        <TableCell sx={{ whiteSpace: 'nowrap' }}>
                                            <Box
                                                component="span"
                                                sx={{
                                                    display: 'inline-flex',
                                                    alignItems: 'center',
                                                    minWidth: 76,
                                                    px: 1,
                                                    py: 0.5,
                                                    borderRadius: 1,
                                                    border: '1px solid',
                                                    fontWeight: 800,
                                                    ...ELAPSED_TONES[tone]
                                                }}
                                            >
                                                {formatElapsedTime(elapsedDays)}
                                            </Box>
                                        </TableCell>
                                        <TableCell align="right">
                                            <Tooltip title="Open Information Portal">
                                                <IconButton
                                                    size="small"
                                                    aria-label={`Open ${project.reference} Information Portal`}
                                                    onClick={() => openInformationPortal(project.reference)}
                                                >
                                                    <LaunchRoundedIcon fontSize="small" />
                                                </IconButton>
                                            </Tooltip>
                                        </TableCell>
                                    </TableRow>
                                );
                            })}
                        </TableBody>
                    </Table>
                </TableContainer>
            </DialogContent>
            <DialogActions>
                <Button onClick={onClose}>Close</Button>
            </DialogActions>
        </Dialog>
    );
}